import { useState, useMemo, useEffect } from 'react';
import {
  ArrowLeftRight,
  Copy,
  Check,
  RefreshCw,
  TrendingUp,
  Building2,
  Globe2,
  Sparkles,
  ShieldCheck,
  FileCheck2,
  Zap
} from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { useForexRate, useForexRates, useForexCurrencies } from '@/shared/hooks/useForex';
import toast from 'react-hot-toast';
import { formatINRPaise } from '@/shared/utils/currency';

interface CurrencyExchangerModalProps {
  open: boolean;
  onClose: () => void;
  defaultBase?: string;
  defaultQuote?: string;
  defaultAmount?: number;
}

const POPULAR_CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', flag: '🇮🇳' },
  { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', flag: '🇦🇪' },
  { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', flag: '🇸🇦' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', flag: '🇨🇦' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵' },
];

export function CurrencyExchangerModal({
  open,
  onClose,
  defaultBase = 'USD',
  defaultQuote = 'INR',
  defaultAmount = 10000,
}: CurrencyExchangerModalProps) {
  const [base, setBase] = useState(defaultBase);
  const [quote, setQuote] = useState(defaultQuote);
  const [amount, setAmount] = useState<number | ''>(defaultAmount);
  const [sourceMode, setSourceMode] = useState<'market' | 'central_bank'>('market');
  
  // Deal buffer & custom rate negotiation
  const [spreadPercent, setSpreadPercent] = useState<number>(0.50); // Default +0.50% bank spread
  const [isCustomRate, setIsCustomRate] = useState(false);
  const [customRateInput, setCustomRateInput] = useState<string>('');
  
  const [copiedTerms, setCopiedTerms] = useState(false);
  const [copiedValue, setCopiedValue] = useState(false);

  // Queries
  const { data: currenciesData } = useForexCurrencies();
  const { 
    data: rateData, 
    isLoading: rateLoading, 
    refetch: refetchRate, 
    isFetching: rateFetching 
  } = useForexRate(base, quote, sourceMode, open);
  
  const { 
    data: benchmarksData, 
    isLoading: benchmarksLoading, 
    refetch: refetchBenchmarks 
  } = useForexRates('INR', 'USD,EUR,AED,GBP,SAR,CAD,SGD,JPY');

  // Sync custom rate input when base spot rate arrives
  useEffect(() => {
    if (rateData?.rate && !isCustomRate) {
      const buffered = rateData.rate * (1 + spreadPercent / 100);
      setCustomRateInput(buffered.toFixed(4));
    }
  }, [rateData?.rate, spreadPercent, isCustomRate]);

  // Combined currency options (defensively parsed)
  const currencyOptions = useMemo(() => {
    if (currenciesData?.currencies) {
      if (Array.isArray(currenciesData.currencies)) {
        return (currenciesData.currencies as any[])
          .map((c: any) => ({
            code: String(c.iso_code || c.code || '').toUpperCase(),
            name: String(c.name || c.iso_code || c.code || ''),
          }))
          .filter((c) => c.code && isNaN(Number(c.code)))
          .sort((a, b) => a.code.localeCompare(b.code));
      }
      if (typeof currenciesData.currencies === 'object') {
        return Object.entries(currenciesData.currencies)
          .map(([code, val]: [string, any]) => {
            const actualCode = String(
              typeof val === 'object' && val !== null ? val.iso_code || val.code || code : code
            ).toUpperCase();
            const actualName = String(
              typeof val === 'string' ? val : val?.name || val?.iso_code || code
            );
            return { code: actualCode, name: actualName };
          })
          .filter((c) => c.code && isNaN(Number(c.code)))
          .sort((a, b) => a.code.localeCompare(b.code));
      }
    }
    return POPULAR_CURRENCIES.map(c => ({ code: c.code, name: c.name }));
  }, [currenciesData]);

  const rawRate = rateData?.rate ?? (sourceMode === 'market' ? 95.60 : 95.12);
  const activeRate = isCustomRate && Number(customRateInput) > 0 
    ? Number(customRateInput) 
    : rawRate * (1 + spreadPercent / 100);

  const numericAmount = typeof amount === 'number' ? amount : 0;
  const convertedTotal = numericAmount * activeRate;
  const bufferValue = numericAmount * (activeRate - rawRate);

  const handleSwap = () => {
    setBase(quote);
    setQuote(base);
    setIsCustomRate(false);
  };

  const handleSelectSpread = (pct: number) => {
    setSpreadPercent(pct);
    setIsCustomRate(false);
    if (rawRate > 0) {
      setCustomRateInput((rawRate * (1 + pct / 100)).toFixed(4));
    }
  };

  const handleCopyDealTerms = () => {
    const text = `Commercial FX Terms: 1 ${base} = ${activeRate.toFixed(4)} ${quote} (${spreadPercent > 0 ? `Live Spot + ${spreadPercent}% Bank Spread` : 'Live Spot Rate'}) | Total Deal Value: ${numericAmount.toLocaleString()} ${base} = ${convertedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${quote} | FX Rate Validity: 7 Calendar Days.`;
    navigator.clipboard.writeText(text);
    setCopiedTerms(true);
    toast.success('Copied Commercial Quotation Terms to clipboard!');
    setTimeout(() => setCopiedTerms(false), 2000);
  };

  const handleCopyValueOnly = () => {
    const text = `${convertedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${quote}`;
    navigator.clipboard.writeText(text);
    setCopiedValue(true);
    toast.success('Copied converted total!');
    setTimeout(() => setCopiedValue(false), 2000);
  };

  const handleRefreshAll = () => {
    refetchRate();
    refetchBenchmarks();
    toast.success('Refreshed real-time market spot rates.');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full pr-6 gap-3">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-violet-100 text-violet-700 border border-violet-200">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-bold text-slate-900">Commercial Deal & Forex Desk</h3>
                {sourceMode === 'market' ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Live Market Spot (Matches Google)
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    ECB Official Reference (Statutory)
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500">Real-time interbank currency exchange for sales quotations & commercial deals</p>
            </div>
          </div>

          {/* Right Controls: Mode Toggle & Refresh */}
          <div className="flex items-center gap-2">
            {/* Source Toggle */}
            <div className="flex items-center p-0.5 rounded-lg bg-slate-100 border border-slate-200 text-xs">
              <button
                type="button"
                onClick={() => {
                  setSourceMode('market');
                  setIsCustomRate(false);
                }}
                className={`px-2.5 py-1 rounded-md font-semibold transition ${
                  sourceMode === 'market'
                    ? 'bg-white text-violet-700 shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Use Live Real-Time Market Rate (Google / Interbank Spot)"
              >
                ⚡ Live Spot
              </button>
              <button
                type="button"
                onClick={() => {
                  setSourceMode('central_bank');
                  setIsCustomRate(false);
                }}
                className={`px-2.5 py-1 rounded-md font-semibold transition ${
                  sourceMode === 'central_bank'
                    ? 'bg-white text-blue-700 shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Use Official Central Bank Reference Fixing (ECB)"
              >
                🏛️ Central Bank
              </button>
            </div>

            <Button
              size="sm"
              variant="ghost"
              onClick={handleRefreshAll}
              className="text-slate-500 hover:text-slate-800"
              title="Refresh live rates"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${rateFetching ? 'animate-spin text-violet-600' : ''}`} />
              <span className="text-xs hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6 pt-2">
        {/* Main Deal Converter Card */}
        <div className="p-5 rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white shadow-xl border border-slate-800">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
            {/* Amount */}
            <div className="md:col-span-4 space-y-1.5">
              <label className="text-xs font-semibold text-slate-300 flex items-center justify-between">
                <span>Deal Amount</span>
                <span className="text-[11px] text-violet-300">Quote Volume</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value === '' ? '' : parseFloat(e.target.value))}
                  placeholder="10000"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-800/90 border border-slate-700 text-white font-mono text-base font-bold focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>

            {/* Base Currency */}
            <div className="md:col-span-3 space-y-1.5">
              <label className="text-xs font-semibold text-slate-300">Client Currency (From)</label>
              <select
                value={base}
                onChange={(e) => {
                  setBase(e.target.value);
                  setIsCustomRate(false);
                }}
                className="w-full px-3 py-2.5 rounded-xl bg-slate-800/90 border border-slate-700 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                {currencyOptions.map((c) => (
                  <option key={c.code} value={c.code} className="bg-slate-900 text-white">
                    {c.code} — {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Swap Button */}
            <div className="md:col-span-2 flex justify-center pt-5 md:pt-4">
              <button
                type="button"
                onClick={handleSwap}
                title="Swap currencies"
                className="p-2.5 rounded-full bg-violet-600 hover:bg-violet-500 text-white shadow-md transition-transform hover:scale-110 active:scale-95 cursor-pointer"
              >
                <ArrowLeftRight className="w-4 h-4" />
              </button>
            </div>

            {/* Quote Currency */}
            <div className="md:col-span-3 space-y-1.5">
              <label className="text-xs font-semibold text-slate-300">Settlement Currency (To)</label>
              <select
                value={quote}
                onChange={(e) => {
                  setQuote(e.target.value);
                  setIsCustomRate(false);
                }}
                className="w-full px-3 py-2.5 rounded-xl bg-slate-800/90 border border-slate-700 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                {currencyOptions.map((c) => (
                  <option key={c.code} value={c.code} className="bg-slate-900 text-white">
                    {c.code} — {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Quick Deal Presets */}
          <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-slate-800/80 overflow-x-auto text-xs text-slate-400">
            <span className="text-[11px] font-medium mr-1 text-slate-400">Deal Presets:</span>
            {[1000, 5000, 10000, 25000, 50000, 100000, 250000].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setAmount(preset)}
                className={`px-2.5 py-0.5 rounded-md text-[11px] font-mono transition cursor-pointer ${
                  amount === preset
                    ? 'bg-violet-600 text-white font-bold'
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                }`}
              >
                {preset.toLocaleString()}
              </button>
            ))}
          </div>

          {/* Deal Safety Buffer & Bank Spread Control */}
          <div className="mt-4 p-3.5 rounded-xl bg-slate-800/60 border border-slate-700/80">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2.5">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-violet-300">
                <ShieldCheck className="w-4 h-4 text-violet-400" />
                <span>Bank Spread & Deal Margin Buffer</span>
                <span className="text-[10px] text-slate-400 font-normal">(Protects profit on settlement)</span>
              </div>
              <div className="text-[11px] text-slate-300">
                Spot Base: <strong className="font-mono text-white">{rawRate.toFixed(4)}</strong>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {[
                { label: '0% (Exact Spot)', value: 0 },
                { label: '+0.25% Buffer', value: 0.25 },
                { label: '+0.50% (Standard Bank Spread)', value: 0.50 },
                { label: '+1.00% (High Protection)', value: 1.00 },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSelectSpread(opt.value)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition cursor-pointer ${
                    !isCustomRate && spreadPercent === opt.value
                      ? 'bg-violet-600 text-white font-bold shadow-xs'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}

              {/* Custom Negotiated Rate Input */}
              <div className="flex items-center gap-1 ml-auto">
                <span className="text-[11px] text-slate-400">Custom Rate:</span>
                <input
                  type="number"
                  step="any"
                  value={customRateInput}
                  onChange={(e) => {
                    setCustomRateInput(e.target.value);
                    setIsCustomRate(true);
                  }}
                  placeholder={rawRate.toFixed(4)}
                  className={`w-28 px-2 py-0.5 rounded-md font-mono text-xs text-white bg-slate-900 border ${
                    isCustomRate ? 'border-amber-400 ring-1 ring-amber-400' : 'border-slate-700'
                  } focus:outline-none`}
                />
              </div>
            </div>
          </div>

          {/* Calculated Deal Total Banner */}
          <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-violet-950/80 via-indigo-950/80 to-slate-900/90 border border-violet-500/40 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-inner">
            <div>
              <div className="text-xs text-violet-300 font-medium flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-violet-400" />
                <span>Commercial Deal Total ({quote})</span>
              </div>
              <div className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight mt-1 font-mono">
                {rateLoading ? (
                  <span className="text-slate-400 text-lg animate-pulse">Calculating live rate...</span>
                ) : (
                  <>
                    {convertedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                    <span className="text-violet-300 text-2xl font-sans">{quote}</span>
                  </>
                )}
              </div>
              
              {/* Rate Breakdown Details */}
              <div className="text-xs text-slate-300 mt-1.5 flex flex-wrap items-center gap-2 font-mono">
                <span className="px-2 py-0.5 rounded bg-violet-900/50 text-violet-200 border border-violet-700/50">
                  Applied: 1 {base} = {activeRate.toFixed(4)} {quote}
                </span>
                <span>•</span>
                <span className="text-slate-400">
                  Base Spot: {rawRate.toFixed(4)}
                </span>
                {spreadPercent > 0 && !isCustomRate && (
                  <>
                    <span>•</span>
                    <span className="text-emerald-400 font-semibold">
                      +{spreadPercent}% Buffer (+{bufferValue > 0 ? bufferValue.toLocaleString(undefined, { maximumFractionDigits: 2 }) : 0} {quote})
                    </span>
                  </>
                )}
                {rateData?.centralBankRate && (
                  <>
                    <span>•</span>
                    <span className="text-slate-400">
                      ECB: {rateData.centralBankRate.toFixed(4)}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Action Buttons: 1-Click Copy Deal Quotation Terms */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
              <Button
                size="sm"
                onClick={handleCopyDealTerms}
                className="bg-violet-600 hover:bg-violet-500 text-white font-bold text-xs shadow-md flex items-center justify-center gap-1.5 py-2 px-3.5"
                title="Copy ready-to-use commercial quotation clause"
              >
                {copiedTerms ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <FileCheck2 className="w-3.5 h-3.5" />}
                <span>{copiedTerms ? 'Copied Terms!' : 'Copy Quotation Terms'}</span>
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={handleCopyValueOnly}
                className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white text-xs flex items-center justify-center gap-1.5 py-2 px-3"
                title="Copy amount only"
              >
                {copiedValue ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedValue ? 'Copied Total' : 'Copy Value'}</span>
              </Button>
            </div>
          </div>

          {/* Feed Metadata */}
          <div className="mt-3 flex flex-wrap items-center justify-between text-[11px] text-slate-400 px-1 gap-2">
            <div className="flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-emerald-400" />
              <span>{rateData?.provider || 'Live Interbank Spot Feed (Matches Google / Morningstar)'}</span>
            </div>
            {rateData?.centralBankRate && (
              <div className="flex items-center gap-1.5 text-slate-400">
                <Building2 className="w-3.5 h-3.5 text-slate-500" />
                <span>ECB Official Fixing: {rateData.centralBankRate.toFixed(4)} {quote}</span>
              </div>
            )}
          </div>
        </div>

        {/* Global Reference Rates Benchmark Table (Against INR) */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe2 className="w-4 h-4 text-violet-600" />
              <h4 className="text-sm font-bold text-slate-800">
                Live Commercial Benchmark Rates against Indian Rupee (INR)
              </h4>
            </div>
            <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Live Interbank Spot Feed
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {[
              { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
              { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺' },
              { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', flag: '🇦🇪' },
              { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧' },
              { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', flag: '🇸🇦' },
              { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', flag: '🇨🇦' },
              { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬' },
              { code: 'JPY', name: 'Japanese Yen (100)', symbol: '¥', flag: '🇯🇵', multiplier: 100 },
            ].map((curr) => {
              const match = benchmarksData?.rates?.find((r) => r.quote === curr.code);
              // In benchmarksData, base is INR, so rate is foreign units per 1 INR.
              // To get 1 Foreign Unit in INR, we do 1 / rate.
              const inrRate = match && match.rate > 0 ? (1 / match.rate) * (curr.multiplier || 1) : null;
              const ecbRate = match && match.centralBankRate && match.centralBankRate > 0 
                ? (1 / match.centralBankRate) * (curr.multiplier || 1) 
                : null;

              return (
                <div
                  key={curr.code}
                  onClick={() => {
                    setBase(curr.code);
                    setQuote('INR');
                    setIsCustomRate(false);
                  }}
                  className="p-3 rounded-xl border border-slate-200 bg-white hover:border-violet-400 hover:bg-violet-50/40 transition cursor-pointer group shadow-2xs"
                  title={`Click to convert ${curr.code} to INR`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-base">{curr.flag}</span>
                    <span className="text-[10px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 group-hover:bg-violet-100 group-hover:text-violet-700">
                      {curr.code}
                    </span>
                  </div>
                  <div className="mt-2">
                    <div className="text-xs text-slate-500 truncate">{curr.name}</div>
                    <div className="text-base font-extrabold text-slate-900 font-mono mt-0.5">
                      {benchmarksLoading ? (
                        <span className="text-xs text-slate-400">Loading...</span>
                      ) : inrRate ? (
                        formatINRPaise(inrRate)
                      ) : (
                        '—'
                      )}
                    </div>
                    {ecbRate && (
                      <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                        ECB: {formatINRPaise(ecbRate)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Commercial Deal Best Practice Advisory Notice */}
        <div className="p-3.5 rounded-xl bg-violet-50 border border-violet-200/80 text-violet-900 text-xs flex items-start gap-2.5">
          <TrendingUp className="w-4 h-4 text-violet-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-semibold text-violet-950">
              Deal-Making Advisory for Sales & Exports:
            </p>
            <p className="text-violet-800 leading-relaxed">
              When closing commercial sales in foreign currency, always add a <strong>+0.25% to +0.50% Bank Spread Buffer</strong>. Indian commercial banks (HDFC, SBI, ICICI) apply their interbank conversion spread upon fund credit, ensuring your business locks in full projected profit without currency slippage.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
}
